/**
 * ApprovalQueue — Stage C approval chips (ACTION-TRIAGE-PLATFORM-PLAN §5.3,
 * TASK-CONNECTIONS-AND-AGENTIC-FLOW §6). Pending work items whose policy
 * verdict is approve_first surface here: one chip per item, expand to see
 * what would execute, one tap stamps the approval (which is itself an
 * audited task_activity event). Nothing sends without this stamp — and the
 * GAS executor independently refuses unapproved external actions.
 */

import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ShieldCheck, Loader2 } from 'lucide-react';
import apiClient from '../../lib/apiClient';
import './ApprovalQueue.css';

const CLASS_LABELS = {
  state_change: 'changes shared state',
  external_send: 'sends externally',
  irreversible: 'irreversible',
};

export default function ApprovalQueue() {
  const queryClient = useQueryClient();
  const [openId, setOpenId] = React.useState(null);

  const { data: itemsData } = useQuery({
    queryKey: ['work-items', 'open'],
    queryFn: () => apiClient.get('/api/v1/work-items?status=open&limit=50'),
    staleTime: 60_000,
  });
  const { data: policyData } = useQuery({
    queryKey: ['work-items', 'policies'],
    queryFn: () => apiClient.get('/api/v1/work-items/policies'),
    staleTime: 5 * 60_000,
  });

  const approve = useMutation({
    mutationFn: (id) => apiClient.post(`/api/v1/work-items/${id}/approve`, {}),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['work-items'] }),
  });

  const policies = policyData?.policies || {};
  const pending = (itemsData?.items || []).filter(
    (i) => policies[i.actionClass] === 'approve_first' && !i.approvedBy
  );
  if (!pending.length) return null;

  return (
    <div className="aq-row" role="region" aria-label="Pending approvals">
      {pending.map((item) => (
        <div key={item.id} className="aq-chip">
          <button
            type="button"
            className="aq-chip-main"
            onClick={() => setOpenId(openId === item.id ? null : item.id)}
          >
            <ShieldCheck size={13} />
            <span>
              Approve: {item.title}
              <em className="aq-class"> · {CLASS_LABELS[item.actionClass] || item.actionClass}</em>
            </span>
          </button>

          {openId === item.id && (
            <div className="aq-pop">
              {item.body && <pre className="aq-body">{item.body}</pre>}
              <div className="aq-meta">
                {item.type}{item.account ? ` · ${item.account}` : ''}
                {item.contactEmail ? ` · to ${item.contactEmail}` : ''}
              </div>
              <button
                type="button"
                className="aq-go"
                disabled={approve.isPending}
                onClick={() => approve.mutate(item.id, { onSuccess: () => setOpenId(null) })}
              >
                {approve.isPending ? <Loader2 size={13} className="aq-spin" /> : 'Approve'}
              </button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
